/**
 * Мышь и разметка.
 *
 * Отдельно от компонента, потому что тут нет ничего от React: это
 * конечный автомат из трёх состояний — ничего не делаем, ставим точки
 * новой фигуры, тянем существующую. Смешанный с жизненным циклом
 * компонента, он читался бы вдвое хуже.
 */

import { insideCaption } from './captions.js';
import { channelDragged, channelOf, channelOutline, insidePolygon, isChannel } from './channels.js';
import type { Candle } from '../state/store.js';
import { distanceToSegment, snapToBar } from './geometry.js';
import type { DrawingLayer } from './layer.js';
import type { Drawing, DrawingPoint } from './types.js';
import { findKind, isMultiClick, pointsNeeded } from './kinds.js';
import { measurerFor, textBox, textStyleOf } from './text-style.js';
import { arrowTail } from './shapes.js';
import { volumeHitOf } from './volume-draw.js';
import { activeLevels, fibOptionsOf, fibSpan } from './fib.js';

/** Насколько близко к фигуре нужно попасть курсором, пикселей. */
export const HIT_RADIUS = 6;
/** Радиус захвата опорной точки. Чуть больше самого квадратика. */
export const HANDLE_RADIUS = 9;
/** Для пальца: он закрывает точку и попадает не туда, куда целился. */
export const TOUCH_HANDLE_RADIUS = 18;

/**
 * Опорная точка под курсором.
 *
 * За неё фигуру растягивают, а не двигают целиком: у прогноза позиции
 * это единственный способ подвинуть стоп, не трогая вход.
 */
export function handleAt(
  layer: DrawingLayer,
  drawing: Drawing,
  x: number,
  y: number,
  radius = HANDLE_RADIUS,
): number | null {
  let best: number | null = null;
  let bestDistance = radius;
  // Опора там, где её нарисовали: у канала — на линии, у стрелки — на хвосте.
  // Опор может быть больше, чем точек: у канала их шесть на три точки.
  const anchors = layer.anchorsOf(drawing.id);
  const spots = anchors ?? drawing.points.map((point) => layer.toScreen(point.time, point.price));

  spots.forEach((at, index) => {
    if (!at) return;
    const distance = Math.hypot(x - at.x, y - at.y);
    if (distance <= bestDistance) {
      best = index;
      bestDistance = distance;
    }
  });

  if (best !== null) return best;

  /*
   * У прогноза позиции уровни тянут за саму линию, а не за квадратик.
   *
   * Бокс большой, квадратики на его углах — семь пикселей: попасть в
   * них трудно, и человек почти всегда захватывал фигуру целиком. Она
   * ехала со всеми тремя ценами разом, отношение риска к прибыли при
   * этом не менялось — со стороны выглядело так, будто расчёт замер.
   */
  if (drawing.kind !== 'long-position' && drawing.kind !== 'short-position') return null;

  const screen = drawing.points.map((point) => layer.toScreen(point.time, point.price));
  const left = Math.min(...screen.filter(present).map((point) => point.x));
  const right = Math.max(...screen.filter(present).map((point) => point.x), left + 60);
  if (x < left - HIT_RADIUS || x > right + HIT_RADIUS) return null;

  let level: number | null = null;
  let levelDistance = HIT_RADIUS;
  screen.forEach((point, index) => {
    if (!point) return;
    const distance = Math.abs(y - point.y);
    if (distance <= levelDistance) {
      level = index;
      levelDistance = distance;
    }
  });

  return level;
}

function present(point: { x: number; y: number } | null): point is { x: number; y: number } {
  return point !== null;
}

/**
 * Фигура под курсором.
 *
 * Перебор идёт с конца: нарисованное позже лежит сверху, и выбирать
 * надо именно его.
 *
 * Кроме одного случая: кружок выделенной фигуры важнее всего, что под
 * ним. Иначе правый конец канала, упавший на соседнюю фигуру, нельзя
 * было схватить — щелчок доставался соседке.
 */
export function hitTest(
  layer: DrawingLayer,
  drawings: readonly Drawing[],
  x: number,
  y: number,
  width: number,
  height: number,
  selectedId: string | null = null,
): Drawing | null {
  const selected = selectedId ? drawings.find((drawing) => drawing.id === selectedId) : undefined;
  if (selected && handleAt(layer, selected, x, y) !== null) return selected;
  for (let i = drawings.length - 1; i >= 0; i -= 1) {
    const drawing = drawings[i]!;
    if (distanceTo(layer, drawing, x, y, width, height) <= HIT_RADIUS) return drawing;
  }
  return null;
}

/**
 * Расстояние до фигуры.
 *
 * Считается по опорным точкам и по отрезкам между ними, а не по
 * настоящему контуру: для попадания курсором этого достаточно, а
 * повторять здесь всю геометрию рисования — верный способ развести
 * нарисованное и нажимаемое.
 */
function distanceTo(
  layer: DrawingLayer,
  drawing: Drawing,
  x: number,
  y: number,
  width: number,
  height: number,
): number {
  const screen = drawing.points
    .map((point) => layer.toScreen(point.time, point.price))
    .filter((point): point is { x: number; y: number } => point !== null);

  const a = screen[0];
  if (!a) return Number.POSITIVE_INFINITY;
  const b = screen[1];

  // Надпись на линии и подсказка «+ Добавьте текст» — часть фигуры:
  // щелчок по ним не должен проваливаться в график.
  const caption = layer.captionBoxOf?.(drawing.id);
  if (caption && insideCaption(caption, x, y)) return 0;

  switch (drawing.kind) {
    // Бесконечные линии ловятся по своей оси на всей ширине экрана.
    case 'horizontal':
      return Math.abs(y - a.y);
    case 'horizontal-ray':
      return x >= a.x ? Math.abs(y - a.y) : Math.hypot(x - a.x, y - a.y);
    case 'vertical':
      return Math.abs(x - a.x);
    case 'cross':
      return Math.min(Math.abs(x - a.x), Math.abs(y - a.y));

    // Лучи и прямые — по продолжению до края холста.
    case 'ray':
    case 'extended': {
      if (!b) return Number.POSITIVE_INFINITY;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const far = dx === 0 ? height * 2 : (dx > 0 ? width - a.x : a.x) / Math.abs(dx);
      const forward = distanceToSegment(x, y, a.x, a.y, a.x + dx * far, a.y + dy * far);
      if (drawing.kind === 'ray') return forward;
      return Math.min(forward, distanceToSegment(x, y, a.x, a.y, a.x - dx * far, a.y - dy * far));
    }

    // Прогноз позиции ловится всей площадью: числа всплывают при
    // наведении на бокс, а не при попадании в точку входа.
    case 'long-position':
    case 'short-position': {
      const prices = screen.map((point) => point.y);
      const top = Math.min(...prices);
      const bottom = Math.max(...prices);
      const left = Math.min(...screen.map((point) => point.x));
      const right = Math.max(...screen.map((point) => point.x), left + 60);

      if (x >= left && x <= right && y >= top && y <= bottom) return 0;
      // Снаружи — обычное расстояние до рамки, чтобы попадать по краю.
      return frameDistance(x, y, { x: left, y: top }, { x: right, y: bottom });
    }

    // Прямоугольные фигуры — по рамке: иначе большая зона перехватывала
    // бы всё, что под ней нарисовано.
    // Прямоугольник ловится и внутри: в нём пишут, и щелчок по середине
    // должен попадать в него, а не в график под ним.
    case 'rect': {
      if (!b) return Number.POSITIVE_INFINITY;
      const inside =
        x >= Math.min(a.x, b.x) && x <= Math.max(a.x, b.x) && y >= Math.min(a.y, b.y) && y <= Math.max(a.y, b.y);
      return inside ? 0 : frameDistance(x, y, a, b);
    }

    /*
     * Каналы ловятся по обеим линиям и по заливке между ними — по тому,
     * что нарисовано. Раньше они ловились ломаной по трём точкам, и
     * правый конец и середина второй линии оставались мёртвыми.
     */
    case 'parallel-channel':
    case 'flat-channel':
    case 'disjoint-channel': {
      const channel = channelOf(drawing.kind, screen);
      if (!channel) return b ? distanceToSegment(x, y, a.x, a.y, b.x, b.y) : Number.POSITIVE_INFINITY;
      if (insidePolygon(x, y, channelOutline(channel))) return 0;
      const [a1, b1] = channel.first;
      const [a2, b2] = channel.second;
      return Math.min(
        distanceToSegment(x, y, a1.x, a1.y, b1.x, b1.y),
        distanceToSegment(x, y, a2.x, a2.y, b2.x, b2.y),
      );
    }

    case 'measure':
    case 'ellipse': {
      if (!b) return Number.POSITIVE_INFINITY;
      return frameDistance(x, y, a, b);
    }

    // Уровень-зона тянется от первой точки до правого края: ловится
    // всей полосой.
    case 'level-zone': {
      if (!b) return Number.POSITIVE_INFINITY;
      const left = Math.min(a.x, b.x);
      const top = Math.min(a.y, b.y);
      const bottom = Math.max(a.y, b.y);
      if (x >= left && y >= top && y <= bottom) return 0;
      return frameDistance(x, y, { x: left, y: top }, { x: width, y: bottom });
    }

    case 'gann-box':
    case 'alert-zone': {
      if (!b) return Number.POSITIVE_INFINITY;
      return frameDistance(x, y, a, b);
    }

    // Линия-оповещение продолжается вправо — ловится как луч.
    case 'alert-line': {
      if (!b) return Number.POSITIVE_INFINITY;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const far = dx === 0 ? height * 2 : (dx > 0 ? width - a.x : a.x) / Math.abs(dx);
      return distanceToSegment(x, y, a.x, a.y, a.x + dx * far, a.y + dy * far);
    }

    /*
     * Сетка Фибоначчи ловится по всем своим полосам, а не только по
     * косой линии построения. Человек видит уровни и тянется к ним —
     * значит, за них и должно браться: наведение показывает опоры, а
     * перетаскивание двигает всю сетку.
     */
    case 'fib-retracement':
    case 'fib-extension': {
      if (!b) return Number.POSITIVE_INFINITY;
      const options = fibOptionsOf(drawing.kind, drawing.fib);
      if (!options) return Number.POSITIVE_INFINITY;

      // Косая линия построения — как и раньше.
      let best = distanceToSegment(x, y, a.x, a.y, b.x, b.y);

      const first = drawing.points[0];
      const second = drawing.points[1];
      if (!first || !second) return best;
      const [from, to] = fibSpan(options, first.price, second.price);

      // Полосы тянутся ровно там, где нарисованы: с тем же продлением.
      const left = options.extend === 'left' || options.extend === 'both' ? 0 : Math.min(a.x, b.x);
      const right =
        options.extend === 'right' || options.extend === 'both' ? width : Math.max(a.x, b.x);

      for (const level of activeLevels(options)) {
        const point = layer.toScreen(first.time, from + (to - from) * level.value);
        if (!point) continue;
        best = Math.min(best, distanceToSegment(x, y, left, point.y, right, point.y));
      }
      return best;
    }

    case 'circle': {
      if (!b) return Number.POSITIVE_INFINITY;
      const radius = Math.hypot(b.x - a.x, b.y - a.y);
      return Math.abs(Math.hypot(x - a.x, y - a.y) - radius);
    }

    // Подписи ловятся по нарисованному, а не по точке привязки: текст
    // рисуется со сдвигом и занимает целую строку, так что клик по
    // самой надписи в точку не попадал — и подпись нельзя было ни
    // выделить, ни сдвинуть.
    case 'text': {
      // Размеры считаются той же функцией, что и при отрисовке, и не
      // ждут следующего кадра: подпись должна нажиматься сразу после
      // того, как её поставили.
      const style = textStyleOf(drawing.textStyle);
      const box = textBox(a, drawing.text ?? '', style, measurerFor(style));
      if (x >= box.left && x <= box.right && y >= box.top && y <= box.bottom) return 0;
      return frameDistance(
        x,
        y,
        { x: box.left, y: box.top },
        { x: box.right, y: box.bottom },
      );
    }

    case 'note':
    case 'trade-plan':
    case 'price-label':
    case 'callout':
    case 'flag':
    case 'icon': {
      const rect = layer.boundsOf(drawing.id);
      if (!rect) return Math.hypot(x - a.x, y - a.y);
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return 0;
      return frameDistance(
        x,
        y,
        { x: rect.left, y: rect.top },
        { x: rect.right, y: rect.bottom },
      );
    }

    // Стрелки вверх и вниз ловятся по нарисованному: место считает сам
    // рисовальщик и оставляет в границах.
    case 'arrow-up':
    case 'arrow-down': {
      const rect = layer.boundsOf(drawing.id);
      if (!rect) {
        const tail = arrowTail(a, b ?? null, drawing.kind === 'arrow-up' ? 'up' : 'down');
        return distanceToSegment(x, y, a.x, a.y, tail.x, tail.y);
      }
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return 0;
      return frameDistance(x, y, { x: rect.left, y: rect.top }, { x: rect.right, y: rect.bottom });
    }

    /*
     * VWAP и профили объёма ловятся по нарисованному: по линии, по
     * гистограмме, по точке контроля и по рамке диапазона. Их точки —
     * только привязка по времени, и по ним одним в фигуру было бы не попасть.
     */
    case 'anchored-vwap':
    case 'volume-profile-range':
    case 'volume-profile-anchored': {
      const hit = volumeHitOf(drawing.id);
      let best = Math.hypot(x - a.x, y - a.y);
      if (!hit) return best;
      for (const rect of hit.rects) {
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) return 0;
      }
      if (hit.frame) best = Math.min(best, frameDistance(x, y, { x: hit.frame.left, y: hit.frame.top }, { x: hit.frame.right, y: hit.frame.bottom }));
      for (const path of hit.lines) {
        for (let i = 1; i < path.length; i += 1) {
          const from = path[i - 1]!;
          const to = path[i]!;
          // Отрезки далеко по горизонтали не проверяются: у линии VWAP их тысячи.
          if (Math.max(from.x, to.x) < x - 12 || Math.min(from.x, to.x) > x + 12) continue;
          best = Math.min(best, distanceToSegment(x, y, from.x, from.y, to.x, to.y));
        }
      }
      return best;
    }

    default: {
      // Остальное — ломаная по опорным точкам.
      if (screen.length < 2) return Math.hypot(x - a.x, y - a.y);
      let best = Number.POSITIVE_INFINITY;
      for (let i = 1; i < screen.length; i += 1) {
        const from = screen[i - 1]!;
        const to = screen[i]!;
        best = Math.min(best, distanceToSegment(x, y, from.x, from.y, to.x, to.y));
      }
      return best;
    }
  }
}

/** Расстояние до рамки прямоугольника, заданного двумя углами. */
function frameDistance(
  x: number,
  y: number,
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  const left = Math.min(a.x, b.x);
  const right = Math.max(a.x, b.x);
  const top = Math.min(a.y, b.y);
  const bottom = Math.max(a.y, b.y);

  return Math.min(
    distanceToSegment(x, y, left, top, right, top),
    distanceToSegment(x, y, right, top, right, bottom),
    distanceToSegment(x, y, right, bottom, left, bottom),
    distanceToSegment(x, y, left, bottom, left, top),
  );
}

/** Точка под курсором с учётом магнита. */
export function pointAt(
  time: number,
  price: number,
  candles: readonly Candle[],
  magnet: boolean,
): DrawingPoint {
  if (!magnet) return { time, price };

  const candle = findCandle(candles, time);
  if (!candle) return { time, price };
  // Притягиваем и время: уровень «по хвосту» относится к конкретному
  // бару, а не к середине между барами.
  return { time: candle.time, price: snapToBar(candle, price) };
}

function findCandle(candles: readonly Candle[], time: number): Candle | null {
  if (candles.length === 0) return null;

  let low = 0;
  let high = candles.length - 1;
  while (high - low > 1) {
    const mid = (low + high) >> 1;
    if (candles[mid]!.time <= time) low = mid;
    else high = mid;
  }

  const a = candles[low]!;
  const b = candles[high]!;
  return Math.abs(time - a.time) <= Math.abs(time - b.time) ? a : b;
}

/** Готова ли заготовка стать фигурой. */
export function isComplete(draft: Drawing): boolean {
  // Щелчок за щелчком заканчивает человек, а не счёт точек.
  if (isMultiClick(draft.kind)) return false;
  return draft.points.length >= pointsNeeded(draft.kind);
}

let counter = 0;

export function nextDrawingId(kind: string): string {
  counter += 1;
  return `${kind}-${Date.now().toString(36)}-${counter}`;
}

/** Замена одной точки — растягивание фигуры за угол. */
/**
 * Новые точки фигуры, когда тянут её опору.
 *
 * Обычно опора — это сама точка: её и заменяем. У каналов опор больше,
 * чем точек, и тянутся они как в привычных терминалах — см. channels.ts.
 */
export function handleDragged(
  kind: string,
  points: readonly DrawingPoint[],
  handle: number,
  target: DrawingPoint,
): DrawingPoint[] {
  if (isChannel(kind)) {
    const moved = channelDragged(
      kind,
      points.map((point) => ({ x: point.time, y: point.price })),
      handle,
      { x: target.time, y: target.price },
    );
    if (moved) return moved.map((point) => ({ time: point.x, price: point.y }));
  }
  /*
   * Прогноз позиции: стоп и цель стоят в правых углах бокса.
   *
   * Правый край бокса — самая поздняя из их точек. Когда стоп тянули
   * вместе со временем, его кружок ехал за мышью вбок, а бокс то рос, то
   * сжимался (владелец, 21.10.2026: «ползунок нижний при передвижении
   * гуляет»). Теперь стоп меняет только цену и остаётся в нижнем правом
   * углу; ширину задаёт цель — стоп переезжает по времени вместе с ней.
   */
  if ((kind === 'long-position' || kind === 'short-position') && points.length >= 3 && (handle === 1 || handle === 2)) {
    const right = handle === 2 ? target.time : Math.max(points[1]!.time, points[2]!.time);
    const next = points.map((point) => ({ ...point }));
    if (handle === 1) next[1] = { time: right, price: target.price };
    else next[2] = target;
    next[1] = { ...next[1]!, time: right };
    next[2] = { ...next[2]!, time: right };
    return next;
  }
  return pointReplaced(points, handle, target);
}

export function pointReplaced(
  points: readonly DrawingPoint[],
  index: number,
  point: DrawingPoint,
): DrawingPoint[] {
  return points.map((entry, i) => (i === index ? point : entry));
}

/** Сдвиг всей фигуры при перетаскивании. */
export function movedBy(
  drawing: Drawing,
  deltaTime: number,
  deltaPrice: number,
): DrawingPoint[] {
  return drawing.points.map((point) => ({
    time: point.time + deltaTime,
    price: point.price + deltaPrice,
  }));
}

/**
 * Что показать, пока фигура не закончена.
 *
 * Подтверждённые точки плюс точка под курсором. Именно здесь была
 * ошибка, из-за которой линейка не натягивалась: точка под курсором
 * заменяла поставленную, и фигура из двух точек всё время оставалась
 * одной — «0 баров» и «0.00%» на экране.
 *
 * При рисовании от руки предпросмотра нет: там точки уже подтверждены
 * движением мыши.
 */
export function previewOf(
  draft: Drawing | null,
  hover: DrawingPoint | null,
  freehand: boolean,
): Drawing | null {
  if (!draft) return null;
  if (freehand || !hover) return draft;
  // Достраиваем и в предпросмотре: бокс позиции должен быть виден
  // целиком, пока его тянут, а не появляться в последний момент.
  return derived({ ...draft, points: [...draft.points, hover] });
}

/**
 * Достройка фигуры по правилу инструмента.
 *
 * Прогноз позиции задают двумя точками — входом и стопом, — а цель
 * ставится сама.
 */
export function derived(drawing: Drawing): Drawing {
  const derive = findKind(drawing.kind)?.derive;
  if (!derive) return drawing;
  return { ...drawing, points: derive(drawing.points, drawing.kind) };
}

/**
 * Смещение точки вслед за мышью.
 *
 * Инструмент может предложить значение — у прогноза позиции это цель на
 * удвоенном расстоянии. Прыжок этой точки под курсор в момент
 * отпускания кнопки дёргает всю фигуру, поэтому она движется на ту же
 * величину, что и мышь, а не встаёт под неё.
 */
export function movedRelative(
  base: DrawingPoint,
  anchor: DrawingPoint,
  cursor: DrawingPoint,
): DrawingPoint {
  return {
    time: base.time + (cursor.time - anchor.time),
    price: base.price + (cursor.price - anchor.price),
  };
}
